import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Check, Crown, Zap, Gift, CreditCard, Building2, Star } from 'lucide-react';
import { Button } from './ui/button';

export const PACKAGES = [
  {
    key: 'free',
    label: 'Free',
    tagline: 'Başlangıç',
    icon: null,
    badgeBg: 'bg-[#F3F4F6]',
    badgeText: 'text-[#6B7280]',
    cardBorder: 'border-[#E6DCCF]',
    cardBg: 'bg-white',
    headerBg: 'bg-[#F6F1EA]',
    headerText: 'text-[#7A6A5C]',
    checkColor: 'text-[#7A6A5C]',
    features: ['Ayda 1 sorgu hakkı'],
    monthlyPrice: null,
    yearlyPrice: null,
  },
  {
    key: 'pro',
    label: 'Pro',
    tagline: 'Aktif Yatırımcı',
    icon: Zap,
    badgeBg: 'bg-[#EDE9FE]',
    badgeText: 'text-[#5B21B6]',
    cardBorder: 'border-[#C4B5FD]',
    cardBg: 'bg-white',
    headerBg: 'bg-[#EDE9FE]',
    headerText: 'text-[#5B21B6]',
    checkColor: 'text-[#5B21B6]',
    features: ['Günde 3 kez sorgu hakkı'],
    monthlyPrice: 3000,
    yearlyPrice: 2500,
  },
  {
    key: 'platinum',
    label: 'Platinum',
    tagline: 'Premium',
    icon: Crown,
    badgeBg: 'bg-[#8f969e]',
    badgeText: 'text-[#3c3e42]',
    cardBorder: 'border-[#8f969e]',
    cardBg: 'bg-white',
    headerBg: 'bg-[#8f969e]',
    headerText: 'text-[#3c3e42]',
    checkColor: 'text-[#3c3e42]',
    features: [
      'Sınırsız sorgu hakkı',
      "Platinum'a özel Simons Analizi",
      'Endeks Analizi',
    ],
    monthlyPrice: 5000,
    yearlyPrice: 3500,
    recommended: true,
  },
];

export const PackageBadge = ({ pkg, size = 'sm' }) => {
  const info = PACKAGES.find(p => p.key === pkg) || PACKAGES[0];
  const sizeClass = size === 'sm' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1';
  return (
    <span className={`inline-flex flex-shrink-0 font-semibold rounded-full leading-none ${sizeClass} ${info.badgeBg} ${info.badgeText}`}>
      {info.label}
    </span>
  );
};

const PaymentModal = ({ open, onOpenChange, pkg, period }) => {
  if (!pkg) return null;
  const price = period === 'yearly' ? pkg.yearlyPrice : pkg.monthlyPrice;
  const periodLabel = period === 'yearly' ? 'Yıllık' : 'Aylık';
  const yearlyTotal = pkg.yearlyPrice ? pkg.yearlyPrice * 12 : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-sm bg-[#F6F1EA] border-[#E6DCCF] p-5">
        <DialogHeader className="mb-4">
          <DialogTitle className="font-['Playfair_Display'] text-[#2E2620] text-lg">
            Ödeme Yöntemi Seçin
          </DialogTitle>
          <DialogDescription asChild>
            <div className="mt-2 flex items-center gap-2 bg-white border border-[#E6DCCF] rounded-lg px-3 py-2">
              <PackageBadge pkg={pkg.key} size="md" />
              <span className="text-sm text-[#2E2620] font-medium">{periodLabel}</span>
              <span className="ml-auto text-sm font-bold text-[#2E2620]">
                {price?.toLocaleString('tr-TR')} ₺
                <span className="text-xs font-normal text-[#7A6A5C]">/ay</span>
              </span>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mb-4">
          <button
            disabled
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-[#E6DCCF] bg-white opacity-60 cursor-not-allowed"
          >
            <CreditCard className="w-5 h-5 text-[#C86F4A]" />
            <div className="text-left">
              <p className="text-sm font-semibold text-[#2E2620]">Iyzico ile Öde</p>
              <p className="text-xs text-[#7A6A5C]">Kredi / Banka Kartı</p>
            </div>
            <span className="ml-auto text-[10px] bg-[#F3F4F6] text-[#6B7280] px-2 py-0.5 rounded-full font-medium">Yakında</span>
          </button>

          <button
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-[#E6DCCF] bg-white hover:border-[#6D7C3B]/50 transition-colors"
          >
            <Building2 className="w-5 h-5 text-[#6D7C3B]" />
            <div className="text-left">
              <p className="text-sm font-semibold text-[#2E2620]">Havale / EFT</p>
              <p className="text-xs text-[#7A6A5C]">Banka transferi</p>
            </div>
          </button>
        </div>

        <div className="bg-white border border-[#E6DCCF] rounded-xl px-4 py-3">
          <p className="text-xs text-[#7A6A5C] leading-relaxed">
            Şu an için{' '}
            <span className="font-semibold text-[#2E2620]">
              almak istediğiniz paketi ({pkg.label} — {periodLabel}
              {period === 'yearly' && yearlyTotal ? `, toplam ${yearlyTotal.toLocaleString('tr-TR')} ₺` : ''})
            </span>{' '}
            ve iletişim bilgilerinizi{' '}
            <a
              href={`https://t.me/emrekirbayir?text=Merhaba%2C%20${encodeURIComponent(pkg.label)}%20paketi%20(${encodeURIComponent(periodLabel)})%20almak%20istiyorum.`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#C86F4A] font-semibold hover:underline"
            >
              admin'e iletiniz.
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PackageModal = ({ open, onOpenChange, currentPkg = 'free' }) => {
  const [period, setPeriod] = useState('monthly');
  const [paymentModal, setPaymentModal] = useState({ open: false, pkg: null });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-2xl bg-[#F6F1EA] border-[#E6DCCF] p-0 max-h-[90vh] flex flex-col">
          <div className="overflow-y-auto flex-1 p-5 sm:p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="font-['Playfair_Display'] text-[#2E2620] text-xl">
              Paket Seçenekleri
            </DialogTitle>
            <DialogDescription className="text-[#7A6A5C] flex items-center gap-2">
              Mevcut paketiniz: <PackageBadge pkg={currentPkg} size="md" />
            </DialogDescription>
          </DialogHeader>

          {/* Aylık / Yıllık toggle */}
          <div className="flex items-center justify-center mb-4">
            <div className="flex bg-white border border-[#E6DCCF] rounded-full p-1 gap-1">
              <button
                onClick={() => setPeriod('monthly')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  period === 'monthly'
                    ? 'bg-[#2E2620] text-white'
                    : 'text-[#7A6A5C] hover:text-[#2E2620]'
                }`}
              >
                Aylık
              </button>
              <button
                onClick={() => setPeriod('yearly')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  period === 'yearly'
                    ? 'bg-[#2E2620] text-white'
                    : 'text-[#7A6A5C] hover:text-[#2E2620]'
                }`}
              >
                Yıllık
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                  period === 'yearly' ? 'bg-[#6D7C3B] text-white' : 'bg-[#6D7C3B]/15 text-[#6D7C3B]'
                }`}>
                  İndirimli
                </span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {PACKAGES.map((pkg) => {
              const isCurrentPkg = currentPkg === pkg.key;
              const Icon = pkg.icon;
              const price = period === 'yearly' ? pkg.yearlyPrice : pkg.monthlyPrice;
              return (
                <div
                  key={pkg.key}
                  className={`relative rounded-xl border-2 overflow-hidden transition-all ${pkg.cardBorder} ${pkg.cardBg} ${
                    pkg.recommended ? 'shadow-md' : ''
                  } ${isCurrentPkg ? 'ring-2 ring-[#C86F4A] ring-offset-1' : ''}`}
                >
                  {/* Önerilen etiketi */}
                  {pkg.recommended && (
                    <div className="absolute top-0 left-0 right-0 flex justify-center">
                      <span className="bg-[#C86F4A] text-white text-[10px] font-bold px-3 py-0.5 rounded-b-lg flex items-center gap-1">
                        <Star className="w-2.5 h-2.5 fill-white" />
                        Önerilen
                      </span>
                    </div>
                  )}

                  {isCurrentPkg && (
                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#C86F4A] flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}

                  <div className={`px-4 py-4 ${pkg.recommended ? 'pt-6' : ''} ${pkg.headerBg}`}>
                    <div className="flex items-center gap-2 mb-0.5">
                      {Icon && <Icon className={`w-4 h-4 ${pkg.headerText}`} />}
                      <span className={`font-bold text-base ${pkg.headerText}`}>{pkg.label}</span>
                    </div>
                    <p className={`text-xs ${pkg.headerText} opacity-70`}>{pkg.tagline}</p>
                  </div>

                  <div className="px-4 pt-3 pb-1">
                    {price ? (
                      <div className="mb-3">
                        <span className="text-2xl font-bold text-[#2E2620]">
                          {price.toLocaleString('tr-TR')} ₺
                        </span>
                        <span className="text-xs text-[#7A6A5C]">/ay</span>
                        {period === 'yearly' && (
                          <p className="text-[10px] text-[#6D7C3B] font-medium mt-0.5">
                            Yıllık {(price * 12).toLocaleString('tr-TR')} ₺ peşin
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="mb-3">
                        <span className="text-2xl font-bold text-[#2E2620]">Ücretsiz</span>
                      </div>
                    )}
                  </div>

                  <div className="px-4 pb-4 space-y-2">
                    {pkg.features.map((feat, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${pkg.checkColor}`} />
                        <span className="text-xs text-[#2E2620] leading-snug">{feat}</span>
                      </div>
                    ))}

                    {price && !isCurrentPkg && (
                      <Button
                        size="sm"
                        onClick={() => {
                          onOpenChange(false);
                          setPaymentModal({ open: true, pkg });
                        }}
                        className="w-full mt-3 bg-[#2E2620] hover:bg-[#1a1510] text-white rounded-full text-xs"
                      >
                        Şimdi Öde
                      </Button>
                    )}
                    {isCurrentPkg && (
                      <div className="w-full mt-3 text-center text-xs text-[#7A6A5C] py-1.5 bg-[#F6F1EA] rounded-full">
                        Mevcut Paketiniz
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* <div className="mt-4 flex items-start gap-2 bg-white border border-[#E6DCCF] rounded-lg px-4 py-3">
            <Gift className="w-4 h-4 text-[#C86F4A] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-[#7A6A5C] leading-relaxed">
              Paket değişikliği için{' '}
              <a
                href="https://t.me/emrekirbayir"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C86F4A] hover:underline font-medium"
              >
                admin ile iletişime geçiniz.
              </a>
            </p>
          </div> */}
          </div>
        </DialogContent>
      </Dialog>

      <PaymentModal
        open={paymentModal.open}
        onOpenChange={(v) => setPaymentModal((p) => ({ ...p, open: v }))}
        pkg={paymentModal.pkg}
        period={period}
      />
    </>
  );
};

export default PackageModal;
